import { useState } from 'react';
import { useNavigate } from 'react-router';
import { GraduationCap, Loader2, Plus, Settings2, Trash2, X } from 'lucide-react';
import {
    useAdminCreateCourseMutation,
    useAdminDeleteCourseMutation,
    useAdminListCoursesQuery,
    useAdminUpdateCourseMutation,
} from '../../store/api/apiSlice';
import type { AdminTrainingCourse, CreateCourseRequest } from '../../types';
import { readableError } from './lib';

export default function Admin() {
    const navigate = useNavigate();
    const { data: courses, isLoading, isError, error } = useAdminListCoursesQuery();
    const [createCourse, { isLoading: isCreating }] = useAdminCreateCourseMutation();
    const [updateCourse] = useAdminUpdateCourseMutation();
    const [deleteCourse] = useAdminDeleteCourseMutation();

    const [showCreate, setShowCreate] = useState(false);
    const [form, setForm] = useState({ title: '', description: '', passingScore: 100 });
    const [formError, setFormError] = useState('');

    const handleCreate = async () => {
        setFormError('');
        if (!form.title.trim()) {
            setFormError('Title is required.');
            return;
        }
        const payload: CreateCourseRequest = { title: form.title.trim(), passingScore: form.passingScore };
        if (form.description.trim()) payload.description = form.description.trim();
        try {
            const created = await createCourse(payload).unwrap();
            setShowCreate(false);
            setForm({ title: '', description: '', passingScore: 100 });
            navigate(`/training/admin/${created.id}`);
        } catch (err) {
            setFormError(readableError(err, 'Could not create the course.'));
        }
    };

    const togglePublish = (course: AdminTrainingCourse) => {
        updateCourse({ id: course.id, body: { isPublished: !course.isPublished } });
    };

    const handleDelete = (course: AdminTrainingCourse) => {
        if (window.confirm(`Delete "${course.title}"? This removes its videos, questions and options.`)) {
            deleteCourse(course.id);
        }
    };

    return (
        <div className="p-4 md:p-8">
            <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
                <div>
                    <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900 md:text-3xl">
                        <GraduationCap className="h-7 w-7 text-[#FF6B35]" /> Training
                    </h1>
                    <p className="mt-1 text-sm text-gray-500">Author courses, lessons and quizzes for agents.</p>
                </div>
                <button
                    type="button"
                    onClick={() => setShowCreate(true)}
                    className="inline-flex items-center gap-2 rounded-full bg-[#FF6B35] px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#ff5722]"
                >
                    <Plus className="h-4 w-4" /> New course
                </button>
            </div>

            {isLoading ? (
                <div className="flex items-center justify-center py-20">
                    <Loader2 className="h-7 w-7 animate-spin text-gray-400" />
                </div>
            ) : isError ? (
                <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
                    {readableError(error, 'Could not load courses.')}
                </div>
            ) : !courses || courses.length === 0 ? (
                <div className="rounded-2xl border border-gray-100 bg-white p-12 text-center">
                    <p className="font-medium text-gray-900">No courses yet</p>
                    <p className="mt-1 text-sm text-gray-500">Create your first course to get started.</p>
                </div>
            ) : (
                <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white">
                    {courses.map((course, i) => (
                        <div
                            key={course.id}
                            className={`flex flex-wrap items-center gap-4 p-4 md:px-6 ${i > 0 ? 'border-t border-gray-100' : ''}`}
                        >
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                    <p className="truncate font-semibold text-gray-900">{course.title}</p>
                                    {course.isPublished ? (
                                        <span className="rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
                                            Published
                                        </span>
                                    ) : (
                                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                                            Draft
                                        </span>
                                    )}
                                </div>
                                <p className="mt-0.5 text-xs text-gray-500">
                                    {course.videos?.length ?? 0} lessons · Pass mark {course.passingScore}%
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => togglePublish(course)}
                                    className="rounded-full border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50"
                                >
                                    {course.isPublished ? 'Unpublish' : 'Publish'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => navigate(`/training/admin/${course.id}`)}
                                    className="inline-flex items-center gap-1.5 rounded-full bg-gray-900 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-gray-800"
                                >
                                    <Settings2 className="h-3.5 w-3.5" /> Manage
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleDelete(course)}
                                    aria-label="Delete course"
                                    className="rounded-full p-2 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
                                >
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create course modal */}
            {showCreate && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
                        <div className="mb-4 flex items-center justify-between">
                            <h2 className="text-lg font-bold text-gray-900">New course</h2>
                            <button type="button" onClick={() => setShowCreate(false)} aria-label="Close">
                                <X className="h-5 w-5 text-gray-400" />
                            </button>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="mb-1.5 block text-sm font-medium text-gray-900">Title</label>
                                <input
                                    value={form.title}
                                    onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                                    placeholder="Field Agent Onboarding"
                                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm outline-none focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35]"
                                />
                            </div>
                            <div>
                                <label className="mb-1.5 block text-sm font-medium text-gray-900">Description</label>
                                <textarea
                                    value={form.description}
                                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                                    rows={3}
                                    placeholder="Core training every agent must complete."
                                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm outline-none focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35]"
                                />
                            </div>
                            <div>
                                <label className="mb-1.5 block text-sm font-medium text-gray-900">
                                    Passing score (%)
                                </label>
                                <input
                                    type="number"
                                    min={1}
                                    max={100}
                                    value={form.passingScore}
                                    onChange={(e) =>
                                        setForm((f) => ({ ...f, passingScore: Number(e.target.value) || 0 }))
                                    }
                                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm outline-none focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35]"
                                />
                            </div>
                            {formError && <p className="text-sm text-red-600">{formError}</p>}
                        </div>
                        <div className="mt-6 flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={() => setShowCreate(false)}
                                className="rounded-full border border-gray-200 px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleCreate}
                                disabled={isCreating}
                                className="inline-flex items-center gap-2 rounded-full bg-[#FF6B35] px-5 py-2.5 text-sm font-medium text-white hover:bg-[#ff5722] disabled:opacity-50"
                            >
                                {isCreating && <Loader2 className="h-4 w-4 animate-spin" />}
                                Create
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
